// @flow
import React from 'react';
import {
  injectIntl,
  defineMessages,
  FormattedMessage,
  intlShape,
  type IntlShape,
} from 'react-intl';
import Icon from 'brastrap/common/icon/Icon';
import Drawer from 'brastrap/global/drawer/Drawer';
import FieldSizes from 'brastrap/common/field-sizes/FieldSizes';
import Sku from 'brastrap/transactional/sku/Sku';
import ShopFinder from 'brastrap/transactional/shop-finder/ShopFinder';
import SectionNav from 'brastrap/common/section-nav/SectionNav';
import Loading from 'brastrap/common/activity/Activity';
import Message from 'brastrap/common/message/Message';
import Button from 'brastrap/common/button/Button';
import Markdown from 'brastrap/editorial/markdown/Markdown';
import Result from './Result';

type Props = {
  errorMessage: string,
  intl: IntlShape,
  isFetching: boolean,
  isVisible: boolean,
  onClose: (SyntheticEvent<HTMLButtonElement>) => void,
  onOpen: (SyntheticEvent<HTMLButtonElement>) => void,
  results: [],
  shopFinder: {},
  sizeSelect: {},
  sku: {},
};

const messages = defineMessages({
  checkStock: {
    id: 'stock-finder.check-stock',
    defaultMessage: 'Find in store',
  },
  guideText: {
    id: 'stock-finder.guide-text',
    defaultMessage: `
      This is is a guide only - please call the shop to reserve the item to guarantee availability. Or, you can order the item into your chosen shop so you can try it on using our **free** Click & Collect service!`,
  },
  fetchingStockLevels: {
    id: 'stock-finder.fetching-stock-levels',
    defaultMessage: 'Fetching stock levels. Please wait...',
  },
  shop: {
    id: 'stock-finder.shop',
    defaultMessage: 'Shop',
  },
  isInStock: {
    id: 'stock-finder.is-in-stock',
    defaultMessage: 'Shops with your item in stock',
  },
  findNearest: {
    id: 'stock-finder.find-nearest',
    defaultMessage: 'Find your nearest shop',
  },
  title: {
    id: 'stock-finder.title',
    defaultMessage: 'Stock Finder',
  },
  selectSize: {
    id: 'stock-finder.select-size',
    defaultMessage: 'Please select a size',
  },
});

/**
 * @param {Object} props
 * @return {*}
 * @constructor
 */
const StockFinder = ({
  errorMessage, // localised by the server, so no need to localise again.
  intl: { formatMessage },
  isFetching,
  isVisible,
  onClose,
  onOpen,
  results = [],
  shopFinder,
  sizeSelect,
  sku,
  ...rest
}: Props) => {
  const fieldSizes = {
    label: formatMessage(messages.selectSize),
    respectStockLevels: false,
    ...sizeSelect,
    useSingleDropdown: true,
  };

  const shopFinderWithFields = {
    ...shopFinder,
    ...rest,
    displayTitle: false,
    modifiers: ['stacked', 'simple'],
  };

  return (
    <div className="c-stock-finder">
      <Button
        icon={<Icon icon="stock-finder" />}
        modifiers={['like-link', 'check-stock']}
        label={formatMessage(messages.checkStock)}
        onClick={onOpen}
        ariaLabel="Open store finder"
      />
      <Drawer
        id="stock-finder"
        isVisible={isVisible}
        modifiers={['right']}
        onClose={onClose}
        title={formatMessage(messages.title)}
      >
        <div className="c-stock-finder__guide">
          <p className="u-center">
            <Markdown content={formatMessage(messages.guideText)} />
          </p>
        </div>
        <Sku {...sku} />
        {errorMessage && (
          <Message type="error" dismissable={false} modifiers={['warn']}>
            {errorMessage}
          </Message>
        )}
        {isFetching ? (
          <div className="u-center">
            <Loading modifiers={['small']} />
            &nbsp;
            <FormattedMessage id="stock-finder.fetching-stock-levels" />
          </div>
        ) : (
          // $FlowFixMe - Need to upgrade Flow to add Fragment support.
          <React.Fragment>
            {fieldSizes.sizes.variants.length > 1 && (
              <FieldSizes {...fieldSizes} />
            )}
            {results && results.length ? (
              <div className="c-stock-finder__results">
                <h3 className="c-stock-finder__results-title">
                  <FormattedMessage id="stock-finder.shop" />
                </h3>
                <SectionNav
                  items={results.map(result => ({
                    label: result.name,
                    note: <Result {...result} />,
                    url: `/shops/${result.slug}`,
                    urlTarget: 'label',
                    modifiers: ['line-separator'],
                  }))}
                  title={formatMessage(messages.isInStock)}
                />
              </div>
            ) : (
              <div className="c-stock-finder__shop-finder">
                <p className="c-field__label">
                  <FormattedMessage id="stock-finder.find-nearest" />
                </p>
                <ShopFinder {...shopFinderWithFields} />
              </div>
            )}
          </React.Fragment>
        )}
      </Drawer>
    </div>
  );
};

StockFinder.propTypes = {
  intl: intlShape,
};

export default injectIntl(StockFinder);
